/**
 * Carousel Selection Scoring - Alpine.js data component for auto-advancing draft pick carousel
 *
 * Encapsulates slideshow behavior: auto-advance, pause on hover, dot navigation,
 * and dynamic height measurement for smooth slide transitions.
 *
 * Usage (in Blade):
 *   x-data="carouselSelectionScoring({{ Js::from($picks) }}, {{ $autoAdvanceMs }})"
 *
 * @param {Array} picks - Array of historical pick objects with scoring data
 * @param {number} autoAdvanceMs - Interval between auto-advances in milliseconds
 * @returns {object} Alpine data component
 */
export default function carouselSelectionScoring(picks, autoAdvanceMs = 6000) {
  return {
    currentIndex: 0,
    picks,
    isPaused: false,
    intervalId: null,
    containerHeight: 0,
    resizeObserver: null,
    isReady: false,

    init() {
      this.startAutoAdvance();
      this.$nextTick(() => {
        this.measureHeight();
        this.setupImageLoadListeners();
      });
      this.resizeObserver = new ResizeObserver(() => this.measureHeight());
      if (this.$refs.slidesArea) {
        this.resizeObserver.observe(this.$refs.slidesArea);
      }
    },

    setupImageLoadListeners() {
      const images = this.$el.querySelectorAll('img');
      const totalImages = images.length;

      // Timeout protection: show carousel after 2s even if no images have loaded
      this._readyTimeoutId = setTimeout(() => this._markReady(), 2000);

      // If no images, mark as ready immediately
      if (totalImages === 0) {
        this._markReady();
        return;
      }

      images.forEach((img) => {
        if (img.complete) {
          this._markReady();
        } else {
          img.addEventListener('load', () => this._handleImageLoad(), {
            once: true,
          });
          img.addEventListener('error', () => this._handleImageError(), {
            once: true,
          });
        }
      });
    },

    _markReady() {
      if (!this.isReady) {
        clearTimeout(this._readyTimeoutId);
        this.isReady = true;
      }
    },

    _handleImageLoad() {
      this.measureHeight();
      this._markReady();
    },

    _handleImageError() {
      this.measureHeight();
      this._markReady();
    },

    destroy() {
      this.stopAutoAdvance();
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
      }
    },

    measureHeight() {
      const slidesArea = this.$refs.slidesArea;
      if (!slidesArea) return;

      const slides = slidesArea.querySelectorAll('.scoring__slide');
      let maxHeight = 0;

      slides.forEach((slide) => {
        const originalPosition = slide.style.position;
        const originalVisibility = slide.style.visibility;
        const originalDisplay = slide.style.display;

        slide.style.position = 'relative';
        slide.style.visibility = 'hidden';
        slide.style.display = 'block';

        maxHeight = Math.max(maxHeight, slide.offsetHeight);

        slide.style.position = originalPosition;
        slide.style.visibility = originalVisibility;
        slide.style.display = originalDisplay;
      });

      this.containerHeight = maxHeight;
    },

    get currentPick() {
      return this.picks[this.currentIndex] || {};
    },

    next() {
      this.currentIndex = (this.currentIndex + 1) % this.picks.length;
    },

    prev() {
      this.currentIndex =
        (this.currentIndex - 1 + this.picks.length) % this.picks.length;
    },

    goTo(index) {
      this.currentIndex = index;
    },

    startAutoAdvance() {
      this.intervalId = setInterval(() => {
        if (!this.isPaused) this.next();
      }, autoAdvanceMs);
    },

    stopAutoAdvance() {
      if (this.intervalId) {
        clearInterval(this.intervalId);
        this.intervalId = null;
      }
    },
  };
}
